import { useState } from 'react';
import { useMutation } from '@apollo/client';
import Query from '@App/components/api/Query';
import HubspotLink from '@App/components/navigation/HubspotLink';
import { HUBSPOT_PAGES } from '@App/routes';
import GetHyperlinePlans from '@App/api/query/GetHyperlinePlans.graphql';
import HyperlineCheckout from '@App/api/mutation/HyperlineCheckout.graphql';

export interface HyperlinePlan {
  /** Identifies the offer end to end: unique across the grid, and all the checkout needs. */
  id: string
  /** The product behind the offer: what pairs the monthly and yearly plans of a same offer. */
  productId: string
  name: string
  /** One-line pitch under the name, null when the Hyperline description has none. */
  description: string | null
  /** Selling points, plain strings: the server has already stripped the markdown and the licence terms. */
  features: string[]
  /** The particular licence terms, their own block; empty when the description carries none. */
  licenceTerms: string[]
  /** Amount in the currency's smallest unit (cents). */
  amount: number
  currency: string
  intervalPeriod: string
  intervalCount: number
  /** Minimum commitment, null when the plan can be cancelled at any time. */
  commitmentPeriod: string | null
  commitmentCount: number | null
  /** Free trial length in days, null when the plan has none. */
  trialDays: number | null
}

const INTERVAL_LABELS: Record<string, string> = {
  days: 'jour',
  weeks: 'semaine',
  months: 'mois',
  years: 'an',
};

const INTERVAL_PLURAL_LABELS: Record<string, string> = {
  days: 'jours',
  weeks: 'semaines',
  months: 'mois',
  years: 'ans',
};

const TOGGLE_LABELS: Record<string, string> = {
  days: 'Quotidien',
  weeks: 'Hebdomadaire',
  months: 'Mensuel',
  years: 'Annuel',
};

/** How many months a period covers, for the periods that are a whole number of them. */
const MONTHS_PER_PERIOD: Record<string, number> = {
  months: 1,
  years: 12,
};

// Longest period first, so the option committing the customer for longest is selected by default.
// Whether that is also the best value depends on how the offers are priced in Hyperline — the grid
// cannot tell, and does not claim to.
const PERIOD_ORDER = ['years', 'months', 'weeks', 'days'];

/** Payment methods accepted for the enterprise offer. Commercial fact, held nowhere else. */
const ENTERPRISE_PAYMENT_METHODS = 'CB · Virement · Prélèvement · Chorus Pro';

/**
 * The enterprise offer has no subscription template — that is how it stays out of the purchasable
 * grid — so nothing about it comes from Hyperline and its card is written here. Kept next to the
 * card that shows it rather than in a config: it is copy, and it changes with the copy.
 */
const ENTERPRISE_DESCRIPTION = 'Pour les structures de toute taille — entreprises, organisations '
  + 'publiques et associations qui produisent du contenu de marque.';

const ENTERPRISE_FEATURES = [
  'Superviseur musical dédié pour vos recherches',
  'Multi-utilisateurs & gestion d’équipe',
  'Support prioritaire & téléphonique',
  'Facturation personnalisée (Chorus Pro)',
];

/**
 * Identifies a billing interval by its period *and* its count, as the server groups offers.
 * On the period alone, "/ mois" and "/ 3 mois" would share a tab and sit side by side.
 */
function intervalKey(plan: HyperlinePlan): string {
  return `${plan.intervalPeriod}/${plan.intervalCount}`;
}

function sortIntervals(plans: HyperlinePlan[]): HyperlinePlan[] {
  const rank = (period: string) => (PERIOD_ORDER.indexOf(period) === -1 ? PERIOD_ORDER.length : PERIOD_ORDER.indexOf(period));

  return [...plans].sort((a, b) => rank(a.intervalPeriod) - rank(b.intervalPeriod) || a.intervalCount - b.intervalCount);
}

function formatToggleLabel(plan: HyperlinePlan): string {
  if (plan.intervalCount > 1) {
    return `${plan.intervalCount} ${INTERVAL_PLURAL_LABELS[plan.intervalPeriod] || plan.intervalPeriod}`;
  }

  return TOGGLE_LABELS[plan.intervalPeriod] || plan.intervalPeriod;
}

function formatPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(amount / 100);
}

function formatInterval(period: string, count: number): string {
  const label = INTERVAL_LABELS[period] || period;

  return count > 1 ? `${count} ${label}` : label;
}

/**
 * Number of months the plan is billed for, null when its interval is not a whole number of them.
 *
 * Everything the card says per month rests on this: without it, a weekly or daily offer would be
 * divided by a month it does not span, and be advertised at a price nobody is charged.
 */
function billedMonths(plan: HyperlinePlan): number | null {
  const months = MONTHS_PER_PERIOD[plan.intervalPeriod];

  return months === undefined ? null : months * plan.intervalCount;
}

/**
 * What the offer costs per month, in cents — the figure the cards are compared on.
 *
 * Offers are compared and sorted on this rather than on the raw amount, otherwise a yearly plan
 * would sort after every monthly one and the cheapest offer would land last.
 */
function monthlyAmount(plan: HyperlinePlan): number {
  const months = billedMonths(plan);

  return months === null ? plan.amount : plan.amount / months;
}

/**
 * Precontractual information: a monthly price carrying a one-year commitment must say so, otherwise
 * the monthly/yearly toggle reads as a free choice between two equivalent options.
 */
function formatCommitment(plan: HyperlinePlan): string | null {
  if (plan.commitmentPeriod === null) {
    return null;
  }

  const count = plan.commitmentCount ?? 1;
  const label = (count > 1 ? INTERVAL_PLURAL_LABELS[plan.commitmentPeriod] : INTERVAL_LABELS[plan.commitmentPeriod]) || plan.commitmentPeriod;

  return `Engagement ${count} ${label}`;
}

/**
 * What is actually charged, and when — said in full whenever it is not what the price above shows.
 *
 * A card announcing "19,99 € par mois" for a plan billed 239,88 € once a year has to say so: the
 * amount debited is the one that must be findable on the card, not only the one that compares well.
 */
function formatBillingNote(plan: HyperlinePlan): string | null {
  const months = billedMonths(plan);

  if (months === null || months === 1) {
    return months === 1 ? 'Facturé mensuellement' : null;
  }

  const every = months === 12 ? 'annuellement' : `tous les ${formatInterval(plan.intervalPeriod, plan.intervalCount)}`;

  return `${formatPrice(plan.amount, plan.currency)} facturés ${every}`;
}

/**
 * The plan name without its "Plan " prefix, for the sentences that inline it.
 *
 * The offers are named "Plan Créateur", "Plan Pro" in Hyperline, and "Tout le plan Plan Créateur"
 * is not a sentence.
 */
function planShortName(name: string): string {
  return name.replace(/^plan\s+/i, '');
}

/**
 * Share of the price saved by taking this interval instead of paying by the month, as a percentage —
 * null when the product is not also sold monthly, or when the longer interval is not cheaper.
 */
function savingRate(plan: HyperlinePlan, plans: HyperlinePlan[]): number | null {
  const monthly = plans.find(candidate => candidate.productId === plan.productId && billedMonths(candidate) === 1);

  if (monthly === undefined || monthly.id === plan.id) {
    return null;
  }

  const rate = Math.round((1 - monthlyAmount(plan) / monthlyAmount(monthly)) * 100);

  return rate > 0 ? rate : null;
}

/**
 * The saving announced next to the selected interval, or null when there is not one single figure to
 * announce.
 *
 * Deliberately silent when the offers disagree: one badge over a grid whose plans save 40% and 20%
 * would be wrong for one of them, and a discount misstated on a pricing page is not a rounding
 * detail. Each card still carries its own billed amount.
 */
function commonSavingRate(visiblePlans: HyperlinePlan[], plans: HyperlinePlan[]): number | null {
  const rates = visiblePlans.map(plan => savingRate(plan, plans)).filter((rate): rate is number => rate !== null);

  if (rates.length === 0 || rates.length !== visiblePlans.length) {
    return null;
  }

  return rates.every(rate => rate === rates[0]) ? rates[0] : null;
}

/** The check mark of the selling point lists. Inline so it inherits the card's colour. */
function CheckIcon() {
  return (
    <svg className="pricing-table__check" width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M4 10.5l4 4 8-9.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface FeatureListProps {
  features: string[]
  /** Name of the plan this one builds on, when its list is written as a delta. */
  inheritsFrom?: string | null
}

/**
 * The selling points, optionally introduced by what the plan inherits.
 *
 * "Tout le plan Créateur, plus :" is presentational — nothing in Hyperline says a plan extends
 * another — and it is only true if the descriptions are written as deltas over the previous offer.
 * That is a copy convention on the client's side, not something this grid can enforce.
 */
function FeatureList({ features, inheritsFrom = null }: FeatureListProps) {
  if (features.length === 0) {
    return null;
  }

  return (
    <>
      {/* Closes what the offer costs and opens what it includes — the mockup rules every card. */}
      <div className="pricing-table__divider" />
      {inheritsFrom && <p className="pricing-table__plan-inherits">
        Tout le plan {planShortName(inheritsFrom)}, plus :
      </p>}
      <ul className="pricing-table__plan-features">
        {/* Indexed on purpose: the list is static, never reordered, and two identical lines in a
            description would otherwise collide on the key. */}
        {features.map((feature, index) => <li key={index}><CheckIcon />{feature}</li>)}
      </ul>
    </>
  );
}

/**
 * The particular licence terms, under the selling points and folded away.
 *
 * They are what the offer allows — productions, exploitation, duration, territory — so a visitor is
 * entitled to read them before paying, and they were missing from the page entirely. Folded, because
 * unfolded they are longer than the offer itself and push the button out of sight: the cards are read
 * side by side, and a wall of terms in each of them is what made this text unreadable in the first
 * place.
 *
 * The summary is the client's own heading, first line of the block, and nothing is relabelled here:
 * the wording of a contractual notice is not ours to paraphrase.
 */
function LicenceTerms({ terms }: { terms: string[] }) {
  if (terms.length === 0) {
    return null;
  }

  const [heading, ...rest] = terms;

  return (
    <details className="pricing-table__plan-licence">
      <summary>{heading}</summary>
      {rest.length > 0 && <ul>
        {/* Indexed like the feature list: static, never reordered, and two identical lines in a
            description would otherwise collide on the key. */}
        {rest.map((term, index) => <li key={index}>{term}</li>)}
      </ul>}
    </details>
  );
}

interface HyperlinePlanCardProps {
  plan: HyperlinePlan
  /** Name of the cheaper plan shown before this one, when there is one. */
  inheritsFrom: string | null
  /** Whether this card carries the "Plus populaire" flag. */
  popular: boolean
}

function HyperlinePlanCard({ plan, inheritsFrom, popular }: HyperlinePlanCardProps) {
  const [checkout, { loading, error }] = useMutation(HyperlineCheckout, {
    variables: { planId: plan.id },
    onCompleted: data => {
      window.location.href = data.hyperlineCheckout.checkoutUrl;
    },
    onError: () => {}, // Errors are rendered below; without a handler the promise rejection is unhandled.
  });

  const commitment = formatCommitment(plan);
  const note = formatBillingNote(plan);
  const months = billedMonths(plan);

  // No treatment of its own beyond the badge: in the mockup the flagged card is a card like the
  // others. Tinting or enlarging it would also have broken the row of prices the eye scans across.
  return <div className="pricing-table__plan">
    {popular && <span className="pricing-table__flag">Plus populaire</span>}
    <h3 className="pricing-table__plan-name">{plan.name}</h3>
    {/* Always rendered, empty or not: its reserved height is what lines the three prices up, and a
        catalogue where one offer has been reworded and the next has not would otherwise stagger
        them. */}
    <p className="pricing-table__plan-description">{plan.description}</p>
    <p className="pricing-table__plan-price">
      {/* Per month whenever the interval is a whole number of months, so the cards compare on the
          same unit; the amount actually billed is right below. */}
      <strong>{formatPrice(months === null ? plan.amount : monthlyAmount(plan), plan.currency)}</strong>
      {months === null
        ? <> / {formatInterval(plan.intervalPeriod, plan.intervalCount)}</>
        : <span className="pricing-table__plan-per"> par mois</span>}
    </p>
    {note && <p className="pricing-table__plan-note">{note}</p>}
    {error && <p className="pricing-table__plan-error">
      Une erreur est survenue, impossible de démarrer le paiement. Veuillez réessayer.
    </p>}
    <button type="button" className="btn btn--primary btn--full" disabled={loading} onClick={() => checkout()}>
      {loading ? 'Redirection…' : 'Commencer'}
    </button>
    {plan.trialDays !== null && <p className="pricing-table__plan-trial">
      Essayez pendant {plan.trialDays} jours gratuitement
    </p>}
    {commitment && <p className="pricing-table__plan-commitment">{commitment}</p>}
    <FeatureList features={plan.features} inheritsFrom={inheritsFrom} />
    <LicenceTerms terms={plan.licenceTerms} />
  </div>;
}

/**
 * Standing card for the offers sold through a quote rather than self-service — the enterprise plan has
 * no subscription template in Hyperline, so it never reaches the grid and this card takes its place.
 * Rendered unconditionally: the need for a quote does not depend on the billing interval the visitor
 * is looking at.
 *
 * Presented as one offer among the others, as the mockup does: same card, only a ghost button instead
 * of a filled one. The `--quote` class marks it without restyling it.
 */
function QuoteCard({ inheritsFrom }: { inheritsFrom: string | null }) {
  return <div className="pricing-table__plan pricing-table__plan--quote">
    <h3 className="pricing-table__plan-name">Plan Entreprise</h3>
    <p className="pricing-table__plan-description">{ENTERPRISE_DESCRIPTION}</p>
    <p className="pricing-table__plan-price">
      <strong>Sur devis</strong>
    </p>
    <p className="pricing-table__plan-note">Tarif selon votre structure</p>
    <HubspotLink route={HUBSPOT_PAGES.DEVIS_ENTREPRISE} className="btn btn--secondary btn--full">
      Discutons de votre projet
    </HubspotLink>
    <p className="pricing-table__plan-payment">{ENTERPRISE_PAYMENT_METHODS}</p>
    <FeatureList features={ENTERPRISE_FEATURES} inheritsFrom={inheritsFrom} />
  </div>;
}

export function HyperlinePlanGrid({ plans }: { plans: HyperlinePlan[] }) {
  // A product may expose several billing intervals: a toggle keeps the grid on a single interval
  // instead of mixing "/mois" and "/an" cards. Grouped on the period *and* its count, as the server
  // does — on the period alone, "/mois" and "/ 3 mois" would land on the same tab, side by side,
  // which is the mixing this toggle exists to avoid.
  // One entry per distinct interval, each keeping a plan to label its tab with.
  const intervals = sortIntervals([...new Map(plans.map(plan => [intervalKey(plan), plan])).values()]);
  const [selectedInterval, setSelectedInterval] = useState(intervals.length > 0 ? intervalKey(intervals[0]) : '');

  // Cheapest first. No order is stored in Hyperline, and the price is the one ranking the offers
  // themselves agree on — it also happens to be the reading order of the mockup.
  const visiblePlans = plans
    .filter(plan => intervalKey(plan) === selectedInterval)
    .sort((a, b) => monthlyAmount(a) - monthlyAmount(b));

  const saving = commonSavingRate(visiblePlans, plans);

  // Positional, not data: nothing records which offer is the most popular. The flag goes on the last
  // self-service card — the top of the ladder before the quote — which is where the mockup puts it,
  // and it needs at least two offers to mean anything.
  const popularIndex = visiblePlans.length > 1 ? visiblePlans.length - 1 : -1;
  const lastPlan = visiblePlans.length > 0 ? visiblePlans[visiblePlans.length - 1] : null;

  return <div className="pricing">
    {intervals.length > 1 &&
      <div className="pricing__billing">
        <div className="pricing__toggle" role="group" aria-label="Périodicité de facturation">
          {intervals.map(interval => {
            const key = intervalKey(interval);

            return <button
              key={key}
              type="button"
              className={`pricing__toggle-option${key === selectedInterval ? ' pricing__toggle-option--active' : ''}`}
              aria-pressed={key === selectedInterval}
              onClick={() => setSelectedInterval(key)}
            >
              {formatToggleLabel(interval)}
            </button>;
          })}
        </div>
        {saving !== null && <span className="pricing__saving">-{saving}%</span>}
      </div>
    }
    <div className="pricing-table">
      {visiblePlans.map((plan, index) =>
        <HyperlinePlanCard
          key={plan.id}
          plan={plan}
          inheritsFrom={index > 0 ? visiblePlans[index - 1].name : null}
          popular={index === popularIndex}
        />,
      )}
      <QuoteCard inheritsFrom={lastPlan === null ? null : lastPlan.name} />
    </div>
  </div>;
}

export default function HyperlinePlanGridQuery() {
  return <Query
    component={HyperlinePlanGrid}
    query={GetHyperlinePlans}
    mapResult={(data: { hyperlinePlans: HyperlinePlan[] }) => ({ plans: data.hyperlinePlans })}
  />;
}
